import { api } from './client';
import type { ApiResponse } from './types';
import { locationUpdateFields } from '../lib/provider-command-keys';

export interface LocationFields {
  name: string; categoryCode: string; city: string; state: string;
  description: string; address: string; phone: string; website?: string;
  kind: 'individual' | 'business';
}
export interface LocationDraft extends Omit<LocationFields, 'categoryCode' | 'description' | 'address' | 'phone' | 'website'> {
  id: string; revision: number; lifecycleStatus: string; submittedAt: string | null;
  category: { code: string; name: string };
  description: string | null; address: string | null; phone: string | null;
  reviewMessage: string | null;
  website: string | null;
}
export interface LocationMatch {
  id: string; name: string; city: string; state: string; isClaimable: boolean; reasons: string[];
}
export type Purpose = 'opening' | 'weekly_offer' | 'seasonal_offer' | 'service_special' | 'event_driven';
export interface CampaignFields {
  purpose: Purpose; funding: 'organic' | 'subsidized' | 'paid';
  headline: string; summary: string; body?: string | null; terms?: string | null;
  metroCode: string; startsAt: string; expiresAt: string;
}
export interface Campaign extends CampaignFields {
  id: string; providerId: string; revision: number; status: string;
  reviewMessage: string | null; provider: { name: string };
}
export interface CampaignReport {
  targetEligibleRenders: number; delivery: { eligibleRenders: number; uniqueMembers: number };
  interest: number; intent: number; outcome: number; chargingEnabled: boolean;
}
export interface Capacity {
  capacityStatus: string; reasonCode: string; recentActiveMemberBand: string;
  targetEligibleRenders: number; chargingEnabled: boolean;
}
export interface RevisionCommand { expectedRevision: number; idempotencyKey: string }
export interface ReviewCommand extends RevisionCommand {
  decision: 'approve' | 'request_changes' | 'reject'; reasonCode: string; memberMessage: string;
}
const segment = encodeURIComponent;
export const growth = {
  matches: (facts: LocationFields) => api.post<ApiResponse<{ matches: LocationMatch[] }>>('/providers/matches', facts),
  drafts: () => api.get<ApiResponse<{ drafts: LocationDraft[] }>>('/providers/drafts'),
  draft: (id: string) => api.get<ApiResponse<LocationDraft>>(`/providers/drafts/${segment(id)}`),
  createDraft: (facts: LocationFields, idempotencyKey: string, acknowledgedMatchIds: string[]) =>
    api.post<ApiResponse<LocationDraft>>('/providers/drafts', { ...facts, idempotencyKey, acknowledgedMatchIds }),
  updateDraft: (id: string, facts: LocationFields, command: RevisionCommand) =>
    api.patch<ApiResponse<LocationDraft>>(`/providers/drafts/${segment(id)}`, { ...locationUpdateFields(facts), ...command }),
  submitDraft: (id: string, command: RevisionCommand, acknowledgedMatchIds: string[]) =>
    api.post<ApiResponse<LocationDraft>>(`/providers/drafts/${segment(id)}/submit`, { ...command, acknowledgedMatchIds }),
  /** Throw away a draft never sent for review. The same key sent again answers the same. */
  discardDraft: (id: string, command: RevisionCommand) =>
    api.post<ApiResponse<{ id: string; discarded: true }>>(`/providers/drafts/${segment(id)}/discard`, command),
  publishDraft: (id: string, command: RevisionCommand, acceptingWork: boolean) =>
    api.post<ApiResponse<LocationDraft>>(`/providers/drafts/${segment(id)}/publish`, { ...command, acceptingWork }),
  pendingDrafts: () => api.get<ApiResponse<{ drafts: LocationDraft[] }>>('/admin/providers/drafts'),
  reviewDraft: (id: string, command: ReviewCommand) =>
    api.post<ApiResponse<LocationDraft>>(`/admin/providers/drafts/${segment(id)}/review`, command),
  campaigns: (id: string) => api.get<ApiResponse<{ campaigns: Campaign[] }>>(`/providers/${segment(id)}/campaigns`),
  campaign: (id: string, campaignId: string) =>
    api.get<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}`),
  createCampaign: (id: string, facts: CampaignFields, idempotencyKey: string) =>
    api.post<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns`, { ...facts, idempotencyKey }),
  updateCampaign: (id: string, campaignId: string, facts: CampaignFields, command: RevisionCommand) =>
    api.patch<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}`, { ...facts, ...command }),
  submitCampaign: (id: string, campaignId: string, command: RevisionCommand) =>
    api.post<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}/submit`, command),
  publishCampaign: (id: string, campaignId: string, command: RevisionCommand) =>
    api.post<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}/publish`, command),
  retractCampaign: (id: string, campaignId: string, command: RevisionCommand, reasonCode: string) =>
    api.post<ApiResponse<Campaign>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}/retract`, { ...command, reasonCode }),
  capacity: (id: string, facts: Pick<CampaignFields, 'metroCode' | 'startsAt' | 'expiresAt'>) =>
    api.get<ApiResponse<Capacity>>(`/providers/${segment(id)}/campaigns/preflight`, facts),
  report: (id: string, campaignId: string) =>
    api.get<ApiResponse<CampaignReport>>(`/providers/${segment(id)}/campaigns/${segment(campaignId)}/report`),
  pendingCampaigns: () => api.get<ApiResponse<{ campaigns: Campaign[] }>>('/admin/providers/campaigns'),
  reviewCampaign: (id: string, command: ReviewCommand) =>
    api.post<ApiResponse<Campaign>>(`/admin/providers/campaigns/${segment(id)}/review`, command),
};
